import React, { useCallback, useEffect, useRef, useState } from 'react';
import { recordScore, getProgress, setLevel, unlockNext, getHighScore } from '../gamesLib';
import { sfx } from '../gameSound';

interface Props {
  onExit: () => void;
  challengeScore?: number | null;
}

// [cols, rows, mines] per level.
const BOARDS: [number, number, number][] = [
  [8, 8, 10],
  [10, 10, 16],
  [12, 12, 26],
  [14, 12, 36],
];

interface Tile {
  mine: boolean;
  revealed: boolean;
  flagged: boolean;
  adj: number;
}

function makeBoard(cols: number, rows: number, mines: number, safeIdx: number): Tile[] {
  const total = cols * rows;
  const tiles: Tile[] = Array.from({ length: total }, () => ({ mine: false, revealed: false, flagged: false, adj: 0 }));
  // Place mines, avoiding the first-click cell.
  let placed = 0;
  while (placed < mines) {
    const i = Math.floor(Math.random() * total);
    if (i === safeIdx || tiles[i].mine) continue;
    tiles[i].mine = true;
    placed++;
  }
  // Compute adjacency.
  const neighbors = (i: number) => {
    const x = i % cols, y = Math.floor(i / cols);
    const out: number[] = [];
    for (let dy = -1; dy <= 1; dy++) for (let dx = -1; dx <= 1; dx++) {
      if (dx === 0 && dy === 0) continue;
      const nx = x + dx, ny = y + dy;
      if (nx >= 0 && nx < cols && ny >= 0 && ny < rows) out.push(ny * cols + nx);
    }
    return out;
  };
  for (let i = 0; i < total; i++) {
    if (tiles[i].mine) continue;
    tiles[i].adj = neighbors(i).filter(n => tiles[n].mine).length;
  }
  return tiles;
}

const NUM_COLORS = ['', '#1976d2', '#388e3c', '#d32f2f', '#7b1fa2', '#ff8f00', '#0097a7', '#5d4037', '#455a64'];

const GameMinesweeper: React.FC<Props> = ({ onExit, challengeScore }) => {
  const prog = getProgress('minesweeper');
  const [level, setLvl] = useState(prog.level || 1);
  const [cols, rows, mines] = BOARDS[Math.min(level - 1, BOARDS.length - 1)];
  const [tiles, setTiles] = useState<Tile[]>(() => makeBoard(cols, rows, mines, -1));
  const [started, setStarted] = useState(false);
  const [over, setOver] = useState(false);
  const [won, setWon] = useState(false);
  const [flags, setFlags] = useState(0);
  const [seconds, setSeconds] = useState(0);
  const [best] = useState(getHighScore('minesweeper'));
  const [flagMode, setFlagMode] = useState(false);
  const savedRef = useRef(false);

  const reset = useCallback((lvl: number) => {
    const [c, r, m] = BOARDS[Math.min(lvl - 1, BOARDS.length - 1)];
    setTiles(makeBoard(c, r, m, -1));
    setStarted(false);
    setOver(false);
    setWon(false);
    setFlags(0);
    setSeconds(0);
    savedRef.current = false;
    setLvl(lvl);
    setLevel('minesweeper', lvl);
  }, []);

  useEffect(() => {
    if (!started || over || won) return;
    const id = setInterval(() => setSeconds(s => s + 1), 1000);
    return () => clearInterval(id);
  }, [started, over, won]);

  const neighborsOf = useCallback((i: number) => {
    const x = i % cols, y = Math.floor(i / cols);
    const out: number[] = [];
    for (let dy = -1; dy <= 1; dy++) for (let dx = -1; dx <= 1; dx++) {
      if (dx === 0 && dy === 0) continue;
      const nx = x + dx, ny = y + dy;
      if (nx >= 0 && nx < cols && ny >= 0 && ny < rows) out.push(ny * cols + nx);
    }
    return out;
  }, [cols, rows]);

  const floodReveal = (arr: Tile[], start: number) => {
    const stack = [start];
    while (stack.length) {
      const i = stack.pop()!;
      const t = arr[i];
      if (t.revealed || t.flagged) continue;
      t.revealed = true;
      if (t.adj === 0 && !t.mine) {
        neighborsOf(i).forEach(n => { if (!arr[n].revealed) stack.push(n); });
      }
    }
  };

  const checkWin = (arr: Tile[]) => arr.every(t => t.mine || t.revealed);

  const reveal = (i: number) => {
    if (over || won) return;
    let arr = tiles;
    // First click: (re)build a board guaranteeing a safe start.
    if (!started) {
      arr = makeBoard(cols, rows, mines, i);
      setStarted(true);
    } else {
      arr = tiles.map(t => ({ ...t }));
    }
    const t = arr[i];
    if (t.flagged || t.revealed) { setTiles(arr); return; }

    if (flagMode) { toggleFlag(i); return; }

    if (t.mine) {
      arr.forEach(x => { if (x.mine) x.revealed = true; });
      setTiles(arr);
      setOver(true);
      sfx.lose();
      return;
    }
    floodReveal(arr, i);
    setTiles(arr);
    sfx.click();
    if (checkWin(arr)) { setWon(true); sfx.win(); }
  };

  const toggleFlag = (i: number) => {
    if (over || won) return;
    setTiles(prev => {
      const arr = prev.map(t => ({ ...t }));
      if (arr[i].revealed) return arr;
      arr[i].flagged = !arr[i].flagged;
      setFlags(arr.filter(x => x.flagged).length);
      return arr;
    });
  };

  const onContext = (e: React.MouseEvent, i: number) => {
    e.preventDefault();
    if (!started) return;
    toggleFlag(i);
  };

  // Persist on end.
  useEffect(() => {
    if ((over || won) && !savedRef.current) {
      savedRef.current = true;
      if (won) {
        // Reward speed + difficulty.
        const score = Math.max(100, mines * 100 + level * 200 - seconds * 5);
        recordScore('minesweeper', score, level);
        unlockNext('minesweeper', level);
      } else {
        recordScore('minesweeper', 0, level);
      }
    }
  }, [over, won, seconds, mines, level]);

  return (
    <div className="gmine">
      <div className="game-toolbar">
        <button className="game-back" onClick={onExit}>← Arcade</button>
        <div className="game-stats">
          <span className="gstat"><b>{mines - flags}</b><i>mines left</i></span>
          <span className="gstat"><b>{seconds}s</b><i>time</i></span>
          <span className="gstat"><b>{best}</b><i>best</i></span>
        </div>
        <button className="game-restart" onClick={() => reset(level)}>↻ New</button>
      </div>

      <div className="g2048-levels">
        {BOARDS.map(([, , m], i) => {
          const lvl = i + 1;
          const unlocked = lvl <= getProgress('minesweeper').unlockedLevel;
          return (
            <button key={lvl} className={`lvl-pill ${level === lvl ? 'active' : ''} ${unlocked ? '' : 'locked'}`}
              onClick={() => unlocked && reset(lvl)} disabled={!unlocked} title={unlocked ? `${m} mines` : 'Locked'}>
              {unlocked ? `Lvl ${lvl}` : '🔒'}
            </button>
          );
        })}
      </div>

      <button className={`gmine-flagtoggle ${flagMode ? 'on' : ''}`} onClick={() => setFlagMode(f => !f)}>
        {flagMode ? '🚩 Flag mode ON' : '⛏️ Dig mode'} <span className="gmine-hint-inline">(tap to switch · right-click also flags)</span>
      </button>

      {challengeScore != null && (
        <div className="challenge-target">🎯 Beat the challenge: <b>{challengeScore}</b> points</div>
      )}

      <div className="gmine-board" style={{ gridTemplateColumns: `repeat(${cols}, 26px)` }}>
        {tiles.map((t, i) => (
          <button
            key={i}
            className={`gmine-cell ${t.revealed ? 'revealed' : ''} ${t.revealed && t.mine ? 'boom' : ''}`}
            onClick={() => reveal(i)}
            onContextMenu={e => onContext(e, i)}
            style={t.revealed && !t.mine && t.adj ? { color: NUM_COLORS[t.adj] } : undefined}
          >
            {t.flagged && !t.revealed ? '🚩' : t.revealed ? (t.mine ? '💣' : t.adj || '') : ''}
          </button>
        ))}
      </div>

      {(over || won) && (
        <div className="game-overlay">
          <div className="game-overlay-card">
            <div className="go-emoji">{won ? '🏆' : '💥'}</div>
            <h3>{won ? `Level ${level} cleared!` : 'Boom!'}</h3>
            <p>{won ? `Solved in ${seconds}s.${level < BOARDS.length ? ' Next level unlocked.' : ''}` : 'You hit a mine.'}</p>
            <div className="go-actions">
              {won && level < BOARDS.length && (
                <button className="g-btn primary" onClick={() => reset(level + 1)}>Next level →</button>
              )}
              <button className="g-btn" onClick={() => reset(level)}>Play again</button>
              <button className="g-btn ghost" onClick={onExit}>Back to arcade</button>
            </div>
          </div>
        </div>
      )}

      <p className="game-hint">Reveal safe tiles, flag the mines. First tap is always safe.</p>
    </div>
  );
};

export default GameMinesweeper;
